/**
 * This file handles the logic for the product configurator feature.
 *
 * This feature includes a button that opens a large dialog which in turn contains a stepper form.
 * The stepper can contain multiple steps, each with their own fields. Which is created
 * using the shopify theme editor.
 */

import {
  createUpsellsApp,
  defineInputWrapperComponent,
  defineMirroredInputComponent,
  defineZipCodeDependantShippingComponent,
  initCardConfiguratorV3,
  maximumLengthHandler,
  useStepperStore,
} from '@nx-paeonia/feature-product-configurator';
import { getDefinitiveNode } from '@nx-paeonia/utils-dom';
import { onClick$ } from '@nx-paeonia/utils-events';
import { GlobalEvents } from './domain/global-events.enum.js';
import { changeTemplateHandler } from './partials/product-config/input.partials.js';
import { refreshConfiguratorOnVariantChange } from './partials/product-config/refresh-configurators.js';
import { initUpsellBlock } from './partials/product-config/upsells.js';

import './partials/product-config/configurator-timeline.component.js';

/* Subscriptions */
useStepperStore().subscribe(async (state, prevState) => {
  const isOpen = state.configurator.isOpen;
  const flyout = getDefinitiveNode<HTMLDialogElement>('.product-config.flyout');

  if (isOpen) {
    flyout.show();
  } else {
    flyout.close();
  }

  if (prevState.steps.activeStep !== state.steps.activeStep) {
    await useStepperStore().getState().steps.showCurrentStep();
    getDefinitiveNode('.product-config-drawer').scrollTo(0, 0);
  }
});

function submitFormHandlerRegister() {
  const form = getDefinitiveNode<HTMLFormElement>('.shopify-product-form');
  form.querySelector<HTMLButtonElement>('[type="submit"]')?.addEventListener('click', async (e) => {
    const activeStep = useStepperStore().getState().steps.activeStep;
    if (!activeStep) {
      return;
    }
    useStepperStore().getState().steps.markStepSubmitted(activeStep);
    const isValid = await useStepperStore().getState().steps.isStepValid(activeStep);

    if (!isValid) {
      e.preventDefault();
    }
  });

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    const activeStep = useStepperStore().getState().steps.activeStep;
    if (activeStep) {
      useStepperStore().getState().steps.markStepSubmitted(activeStep);
      const isValid = await useStepperStore().getState().steps.isStepValid(activeStep);

      if (!isValid) {
        event.preventDefault();
        return;
      }
    }

    const buttonElement = getDefinitiveNode<HTMLButtonElement>('[data-action="add-to-cart"]');
    buttonElement.disabled = true;

    await useStepperStore().getState().form.submit();
  });
}

/* Events */
onClick$('[data-action="open-personalizer"]', async () => {
  await useStepperStore().getState().configurator.open();
});

onClick$('.product-config-header-banner img', async () => {
  await useStepperStore().getState().configurator.close();
});

onClick$('.product-config .backdrop', async () => {
  await useStepperStore().getState().configurator.close();
});

onClick$('.product-config [data-action="next-step"]', async () => {
  await useStepperStore().getState().steps.goToNextStep();
});

onClick$('.product-config [data-action="previous-step"]', async () => {
  await useStepperStore().getState().steps.goToPreviousStep();
});

onClick$('.product-config-stepper-footer [data-action="close"]', async () => {
  await useStepperStore().getState().configurator.close();
});

document.addEventListener('DOMContentLoaded', () => {
  maximumLengthHandler();
  changeTemplateHandler();
  refreshConfiguratorOnVariantChange();
  submitFormHandlerRegister();
  initUpsellBlock();
  try {
    initCardConfiguratorV3();
    createUpsellsApp();
  } catch {}
});

function prepareStepper() {
  getDefinitiveNode<HTMLDialogElement>('.product-config.flyout')
    .querySelectorAll<HTMLDivElement>('.product-config-step')
    .forEach((step, idx) => {
      useStepperStore()
        .getState()
        .steps.registerStep({
          stepId: step.id,
          isSubmitted: false,
          position: idx,
          title: step.dataset.title || `Step ${idx + 1}`,
        });
    });
}

prepareStepper();

defineInputWrapperComponent();
defineMirroredInputComponent();
defineZipCodeDependantShippingComponent();

document.body.addEventListener(GlobalEvents.ProductConfiguratorReinitialized, () => {
  maximumLengthHandler();
  changeTemplateHandler();
  initUpsellBlock();
  try {
    initCardConfiguratorV3();
    createUpsellsApp();
  } catch {}
});

document.addEventListener('productConfigurator:opened', () => {
  prepareStepper();
});
